import { TableKeys, PriorValues } from '../types';
import { roundToDecimal, calculateDivision } from './helpers';
import { COMMA_OPTIONS, INT_TABLE_KEYS, COMMA_TABLE_KEYS } from './constants';

export const calculateCellValue = (
  rowId: TableKeys,
  values: Record<TableKeys, number | null>,
  priorValues: PriorValues
): string => {
  let value: number | null = null;

  const mappedRevenue = values.annualized_revenue || values.revenue;
  const averageArr =
    values.bop_arr === null && values.ending_arr === null
      ? null
      : calculateDivision((values.bop_arr || 0) + (values.ending_arr || 0), 2);

  switch (rowId) {
    // KEY FINANCIAL SECTIONS
    case TableKeys.BOP_ARR:
      value =
        priorValues.ending_arr === null || priorValues.ending_arr === undefined
          ? values.bop_arr
          : priorValues.ending_arr;
      break;
    case TableKeys.NEW_LOGO_ARR:
      value = values.new_logo_arr;
      break;
    case TableKeys.EXPANSION_ARR:
      value = values.expansion_arr;
      break;
    case TableKeys.GROSS_NEW_ARR:
      value =
        values.new_logo_arr === null && values.expansion_arr === null
          ? null
          : (values.new_logo_arr || 0) + (values.expansion_arr || 0);
      break;
    case TableKeys.GROSS_CHURN_ARR:
      value = values.gross_churn_arr;
      break;
    case TableKeys.NET_NEW_ARR:
      value =
        values.gross_new_arr === null && values.gross_churn_arr === null
          ? null
          : (values.gross_new_arr || 0) + (values.gross_churn_arr || 0);
      break;
    case TableKeys.ENDING_ARR:
      value =
        values.bop_arr === null && values.net_new_arr === null
          ? null
          : (values.bop_arr || 0) + (values.net_new_arr || 0);
      break;
    case TableKeys.REVENUE:
      value = values.revenue;
      break;
    case TableKeys.ANNUALIZED_REVENUE:
      value =
        values.ending_arr ||
        (values.revenue !== null ? values.revenue * 4 : null);
      break;
    case TableKeys.TOTAL_GROSS_PROFIT:
      value = values.total_gross_profit;
      break;
    case TableKeys.GROSS_PROFIT_MARGIN:
      value =
        values.total_gross_profit === null ||
        values.total_gross_profit === undefined ||
        values.revenue === null ||
        values.revenue === undefined
          ? null
          : calculateDivision(values.total_gross_profit, values.revenue, true);
      break;
    case TableKeys.TOTAL_OPEX_SM:
      value = values.total_opex_sm;
      break;
    case TableKeys.TOTAL_OPEX_RD:
      value = values.total_opex_rd;
      break;
    case TableKeys.TOTAL_OPEX_GA:
      value = values.total_opex_ga;
      break;
    case TableKeys.TOTAL_OPEX:
      value =
        values.total_opex_sm === null &&
        values.total_opex_rd === null &&
        values.total_opex_ga === null
          ? null
          : (values.total_opex_sm || 0) +
            (values.total_opex_rd || 0) +
            (values.total_opex_ga || 0);
      break;
    case TableKeys.FCF:
      value = values.fcf;
      break;
    case TableKeys.FCF_MARGIN:
      value =
        values.fcf === null ||
        values.fcf === undefined ||
        values.revenue === null ||
        values.revenue === undefined
          ? null
          : calculateDivision(values.fcf, values.revenue, true);
      break;
    case TableKeys.TOTAL_CUSTOMERS:
      value = values.total_customers;
      break;
    case TableKeys.GROSS_NEW_CUSTOMERS_IN_PERIOD:
      value = values.gross_new_customers_in_period;
      break;
    case TableKeys.ARR_PER_CUSTOMER:
      value =
        values.total_customers === null ||
        values.total_customers === undefined ||
        mappedRevenue === null ||
        mappedRevenue === undefined
          ? null
          : calculateDivision(
              calculateDivision(
                mappedRevenue * 1000000,
                values.total_customers
              ),
              1000
            );
      break;
    case TableKeys.TOTAL_HEADCOUNT_SM:
      value = values.total_headcount_sm;
      break;
    case TableKeys.TOTAL_HEADCOUNT_RD:
      value = values.total_headcount_rd;
      break;
    case TableKeys.TOTAL_HEADCOUNT_GA:
      value = values.total_headcount_ga;
      break;
    case TableKeys.TOTAL_HEADCOUNT:
      value =
        values.total_headcount_sm === null &&
        values.total_headcount_rd === null &&
        values.total_headcount_ga === null
          ? null
          : (values.total_headcount_sm || 0) +
            (values.total_headcount_rd || 0) +
            (values.total_headcount_ga || 0);
      break;
    case TableKeys.TOTAL_HEADCOUNT_OTHER:
      value = values.total_headcount_other;
      break;

    // GROWTH AND RETENTION
    case TableKeys.YOY_ARR_GROWTH:
      value =
        values.ending_arr === null ||
        values.ending_arr === undefined ||
        priorValues.last_year_ending_arr === null ||
        priorValues.last_year_ending_arr === undefined ||
        priorValues.last_year_ending_arr === 0
          ? null
          : calculateDivision(
              values.ending_arr,
              priorValues.last_year_ending_arr || 0,
              true,
              true
            );

      break;
    case TableKeys.COMPOSITION_NEW_LOGO:
      value =
        values.new_logo_arr === null ||
        values.new_logo_arr === undefined ||
        values.gross_new_arr === null ||
        values.gross_new_arr === undefined
          ? null
          : calculateDivision(values.new_logo_arr, values.gross_new_arr, true);
      break;
    case TableKeys.COMPOSITION_EXPANSION:
      value =
        values.expansion_arr === null ||
        values.expansion_arr === undefined ||
        values.gross_new_arr === null ||
        values.gross_new_arr === undefined
          ? null
          : calculateDivision(values.expansion_arr, values.gross_new_arr, true);
      break;
    case TableKeys.NEW_LOGO_VELOCITY:
      value =
        values.new_logo_arr === null ||
        values.new_logo_arr === undefined ||
        priorValues.new_logo_arr === null ||
        priorValues.new_logo_arr === undefined ||
        priorValues.new_logo_arr === 0
          ? null
          : calculateDivision(
              values.new_logo_arr,
              priorValues.new_logo_arr || 0,
              true,
              true
            );
      break;
    case TableKeys.QUICK_RATIO:
      value =
        values.gross_new_arr === null ||
        values.gross_new_arr === undefined ||
        values.gross_churn_arr === null ||
        values.gross_churn_arr === undefined
          ? null
          : calculateDivision(
              values.gross_new_arr,
              Math.abs(values.gross_churn_arr)
            );
      break;
    case TableKeys.GROSS_DOLLAR_RETENTION:
      value =
        values.gross_churn_arr === null ||
        values.gross_churn_arr === undefined ||
        values.bop_arr === null ||
        values.bop_arr === undefined ||
        values.ending_arr === null ||
        values.ending_arr === undefined ||
        averageArr === null ||
        averageArr === 0
          ? null
          : (1 + calculateDivision(values.gross_churn_arr * 4, averageArr)!) *
            100;
      break;
    case TableKeys.NET_DOLLAR_RETENTION:
      value =
        values.net_new_arr === null ||
        values.net_new_arr === undefined ||
        values.bop_arr === null ||
        values.bop_arr === undefined ||
        values.ending_arr === null ||
        values.ending_arr === undefined ||
        values.new_logo_arr === null ||
        values.new_logo_arr === undefined ||
        averageArr === null ||
        averageArr === 0
          ? null
          : (1 +
              calculateDivision(
                (values.net_new_arr - values.new_logo_arr) * 4,
                averageArr
              )!) *
            100;
      break;

    // OPERATIONAL EFFICIENCY
    case TableKeys.NET_MAGIC_NUMBER:
      value =
        values.net_new_arr === null ||
        values.net_new_arr === undefined ||
        priorValues.total_opex_sm === null ||
        priorValues.total_opex_sm === undefined ||
        priorValues.total_opex_sm === 0
          ? null
          : calculateDivision(
              values.net_new_arr,
              priorValues.total_opex_sm || 0
            );
      break;
    case TableKeys.RULE_OF_40:
      value =
        values.fcf_margin === null ||
        values.fcf_margin === undefined ||
        values.yoy_arr_growth === null ||
        values.yoy_arr_growth === undefined
          ? null
          : values.fcf_margin + values.yoy_arr_growth;
      break;
    case TableKeys.CAC:
      value =
        values.total_opex_sm === null ||
        values.total_opex_sm === undefined ||
        values.gross_new_customers_in_period === null ||
        values.gross_new_customers_in_period === undefined
          ? null
          : calculateDivision(
              values.total_opex_sm * 1000000,
              values.gross_new_customers_in_period
            );
      break;
    case TableKeys.CAC_PAYBACK_PERIOD:
      value =
        values.cac === null ||
        values.cac === undefined ||
        values.arr_per_customer === null ||
        values.arr_per_customer === undefined ||
        values.gross_profit_margin === null ||
        values.gross_profit_margin === undefined
          ? null
          : calculateDivision(
              values.cac,
              values.arr_per_customer *
                1000 *
                (values.gross_profit_margin / 100)
            );
      break;
    case TableKeys.BURN_MULTIPLE:
      value =
        values.fcf === null ||
        values.fcf === undefined ||
        !(values.fcf < 0) ||
        values.net_new_arr === null ||
        values.net_new_arr === undefined ||
        !(values.net_new_arr > 0)
          ? null
          : calculateDivision(Math.abs(values.fcf), values.net_new_arr);
      break;
    case TableKeys.PERC_OPEX_SM:
      value =
        values.total_opex_sm === null ||
        values.total_opex_sm === undefined ||
        values.revenue === null ||
        values.revenue === undefined
          ? null
          : calculateDivision(values.total_opex_sm, values.revenue, true);
      break;
    case TableKeys.PERC_OPEX_RD:
      value =
        values.total_opex_rd === null ||
        values.total_opex_rd === undefined ||
        values.revenue === null ||
        values.revenue === undefined
          ? null
          : calculateDivision(values.total_opex_rd, values.revenue, true);
      break;
    case TableKeys.PERC_OPEX_GA:
      value =
        values.total_opex_ga === null ||
        values.total_opex_ga === undefined ||
        values.revenue === null ||
        values.revenue === undefined
          ? null
          : calculateDivision(values.total_opex_ga, values.revenue, true);
      break;

    // PROFITABILITY AND CASH FLOW
    case TableKeys.GROSS_PROFIT_MARGIN_1:
      value =
        values.total_gross_profit === null ||
        values.total_gross_profit === undefined ||
        values.revenue === null ||
        values.revenue === undefined
          ? null
          : calculateDivision(values.total_gross_profit, values.revenue, true);
      break;
    case TableKeys.FCF_MARGIN_1:
      value =
        values.fcf === null ||
        values.fcf === undefined ||
        values.revenue === null ||
        values.revenue === undefined
          ? null
          : calculateDivision(values.fcf, values.revenue, true);
      break;

    // HEADCOUNT
    case TableKeys.ARR_PER_FTE:
      value =
        mappedRevenue === null ||
        mappedRevenue === undefined ||
        values.total_headcount === null ||
        values.total_headcount === undefined
          ? null
          : calculateDivision(
              calculateDivision(
                mappedRevenue * 1000000,
                values.total_headcount
              ),
              1000
            );
      break;
    case TableKeys.PERC_HEADCOUNT_SM:
      value =
        values.total_headcount_sm === null ||
        values.total_headcount_sm === undefined ||
        values.total_headcount === null ||
        values.total_headcount === undefined
          ? null
          : calculateDivision(
              values.total_headcount_sm,
              values.total_headcount,
              true
            );
      break;
    case TableKeys.PERC_HEADCOUNT_RD:
      value =
        values.total_headcount_rd === null ||
        values.total_headcount_rd === undefined ||
        values.total_headcount === null ||
        values.total_headcount === undefined
          ? null
          : calculateDivision(
              values.total_headcount_rd,
              values.total_headcount,
              true
            );
      break;
    case TableKeys.PERC_HEADCOUNT_GA:
      value =
        values.total_headcount_ga === null ||
        values.total_headcount_ga === undefined ||
        values.total_headcount === null ||
        values.total_headcount === undefined
          ? null
          : calculateDivision(
              values.total_headcount_ga,
              values.total_headcount,
              true
            );
      break;
    case TableKeys.PERC_HEADCOUNT_OTHER:
      value =
        values.total_headcount_other === null ||
        values.total_headcount_other === undefined ||
        values.total_headcount === null ||
        values.total_headcount === undefined
          ? null
          : calculateDivision(
              values.total_headcount_other,
              values.total_headcount,
              true
            );
      break;
    case TableKeys.OPEX_PER_FTE:
      value =
        values.total_opex === null ||
        values.total_opex === undefined ||
        values.total_headcount === null ||
        values.total_headcount === undefined
          ? null
          : calculateDivision(
              calculateDivision(
                values.total_opex * 1000000 * 4,
                values.total_headcount
              ),
              1000
            );
      break;
  }

  const isIntKey = INT_TABLE_KEYS.includes(rowId);
  const isCommaKey = COMMA_TABLE_KEYS.includes(rowId);

  return value === null || value === undefined
    ? ''
    : isIntKey && isCommaKey
    ? value.toLocaleString()
    : isCommaKey
    ? value.toLocaleString('en-US', COMMA_OPTIONS)
    : isIntKey
    ? roundToDecimal(value, 0)
    : roundToDecimal(value);
};
